import React from "react";
import { IndexTable, Text } from "@shopify/polaris";
import { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import { ICustomerRelationshipShallow } from "@smartrr/shared/entities/CustomerRelationship";
import { extractShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { IdContainer } from "@vendor-app/app/_sharedComponents/TypedTable/usePolarisTypedTable";
import { navigateToCustomerDetails } from "@vendor-app/utils/navigateWithShopInQuery";
import { useCustomerList } from "../../context/CustomerListContext";

const TableHeadings: NonEmptyArray<IndexTableHeading> = [
  { id: "table-column-customer-id", title: "Customer ID" },
  { id: "table-column-customer-name", title: "Customer Name" },
  { id: "table-column-email", title: "Email" },
  { id: "table-column-total-orders", title: "Total orders" },
  { id: "table-column-total-spent", title: "Total spent" },
];

interface TableRowProps {
  customer: ICustomerRelationshipShallow;
  position: number;
}

function TableRow({ customer, position }: TableRowProps) {
  const customerId = extractShopifyId(customer.shopifyId) ?? "";
  const customerName = `${customer.firstName} ${customer.lastName}`;
  const totalSpent = formatMoney(customer.totalSpent, customer.customerCurrency);

  return (
    <IndexTable.Row key={customer.id} id={customer.id} position={position}>
      <IndexTable.Cell>
        <IdContainer onClick={() => customerId && navigateToCustomerDetails(customerId, false)}>
          <Text variant="bodyMd" as="span" fontWeight="semibold">
            #{customerId}
          </Text>
        </IdContainer>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {customerName}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {customer.email}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {customer.orderCount}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {totalSpent}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
}

export const CustomerTable = () => {
  const { state } = useCustomerList();
  const { customers, isLoading } = state;

  return (
    <IndexTable
      selectable={false}
      loading={isLoading}
      headings={TableHeadings}
      itemCount={customers.length}
      emptyState={
        <Text variant="bodyLg" as="span" color="subdued">
          No customers found
        </Text>
      }
      resourceName={{
        singular: "customer",
        plural: "customers",
      }}
    >
      {customers.map((customer, index) => (
        <TableRow key={customer.shopifyId} customer={customer} position={index} />
      ))}
    </IndexTable>
  );
};
